<script setup lang="ts">
  import { computed, getCurrentInstance, ref } from 'vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import V2ReservationTablesEditor from '@/components/v2/reservations/tables/V2ReservationTablesEditor.vue';
  import { useReservationGuide } from '@/composition/reservations/reservation-guide-context';
  import { useReservationTablesCreate } from '@/composition/reservations/use-reservation-tables-create';
  import { useAuthStore } from '@/composition/use-auth';
  import { nextTableNames } from '@/domain/reservations/reservation-guide';
  import {
    DEFAULT_TABLE_SEATS,
    MAX_CREATED_TABLES,
    TABLE_SEATS_MAX,
    TABLE_SEATS_MIN,
  } from '@/domain/reservations/reservation-tables-draft';
  import { PermissionType } from '@/enums/PermissionType';
  import V2ReservationsGuideCreateTables from './V2ReservationsGuideCreateTables.vue';

  /** Step 2 · Mesas: creates the tables of a zero-table branch (2a) or edits seats on the plan (2b). */
  const DEFAULT_TABLE_COUNT = 10;
  const context = useReservationGuide();
  const auth = useAuthStore();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const creator = useReservationTablesCreate(context.scope);
  // The seats follow the shared global value, so a re-mount (Atrás → Mesas) shows the same number.
  const createPayload = ref({
    count: DEFAULT_TABLE_COUNT,
    seats: context.allSeats.value,
  });

  const configuration = computed(() => context.configuration.value);
  const busy = computed(() => context.busy.value);
  const canCreate = computed(() =>
    auth.permissionSet.has(PermissionType.TablesManage)
  );
  const storedTables = computed(() => configuration.value?.tables ?? []);
  const draftTables = computed(() => context.tablesDraft.tables.value);
  // 2a stays while a run is in progress or failed, even once some tables exist.
  const creating = computed(
    () =>
      storedTables.value.length === 0 ||
      creator.progress.value !== null ||
      creator.failed.value
  );
  const validCount = computed(() => {
    const { count, seats } = createPayload.value;
    return (
      Number.isInteger(count) &&
      count >= 1 &&
      count <= MAX_CREATED_TABLES &&
      Number.isInteger(seats) &&
      seats >= TABLE_SEATS_MIN &&
      seats <= TABLE_SEATS_MAX
    );
  });
  /** D19: one enabled table with seats whose draft mode is not «none». */
  const gate = computed(() =>
    draftTables.value.some(
      (table) =>
        table.enabled && table.capacity > 0 && table.reservationsMode !== 'none'
    )
  );
  const ready = computed(() =>
    creating.value ? canCreate.value && validCount.value : gate.value
  );
  /** The bar's CTA label when it is not the plain «Continuar». */
  const nextLabel = computed<string | null>(() => {
    if (creating.value) {
      const progress = creator.progress.value;
      return progress
        ? tr('reservations.guide.tables.creating', {
            done: progress.done,
            total: progress.total,
          })
        : tr('reservations.guide.tables.createAction');
    }
    return gate.value ? null : tr('reservations.guide.tables.continueBlocked');
  });
  const helpKey = computed(() =>
    context.seatlessOnOpen.value
      ? 'reservations.guide.tables.helpSeatless'
      : 'reservations.guide.tables.helpSeated'
  );
  const excludedIds = computed(() => context.excludedIds.value);
  const allSeats = computed(() => context.allSeats.value);

  function onCreateChange(payload: { count: number; seats: number }) {
    createPayload.value = payload;
    // What each table will seat: the global row starts there once the plan shows.
    context.allSeats.value = payload.seats;
  }
  /** Creates what is still missing (a retry recomputes the names from the reloaded bundle). */
  async function createTables() {
    if (!canCreate.value || !validCount.value || busy.value) return;
    const { count, seats } = createPayload.value;
    await context.run(async () => {
      if (!(await context.reload())) return false;
      const before = context.configuration.value;
      if (!before) return false;
      const missing = count - before.tables.length;
      const names =
        missing > 0
          ? nextTableNames(
              before.tables.map((table) => table.name),
              missing,
              (n) => tr('reservations.guide.tables.createName', { n })
            )
          : [];
      if (names.length && !(await creator.create(names, seats))) return false;
      await context.reload();
      const after = context.configuration.value;
      if (!after) return false;
      // A restored namesake may come back with an old mode: the draft puts it online.
      const created = new Set(names.map((name) => name.trim().toLowerCase()));
      const offline = after.tables
        .filter(
          (table) =>
            created.has(table.name.trim().toLowerCase()) &&
            table.reservationsMode !== 'online'
        )
        .map((table) => table.id);
      context.allSeats.value = seats;
      if (offline.length) context.tablesDraft.setMode(offline, 'online', seats);
      creator.failed.value = false;
      return true;
    }, tr('reservations.tables.error'));
  }
  /** One batch write of the touched tables, then the reload; nothing dirty sends nothing. */
  async function continueEditor() {
    if (!gate.value || busy.value) return;
    const result = await context.run(async () => {
      if (context.tablesDraft.dirty.value) {
        // The draft reports the failure itself through the shell's onError.
        if (!(await context.tablesDraft.save())) return false;
        if (!(await context.reload())) return false;
        context.clearExcluded();
      }
      return true;
    }, tr('reservations.tables.error'));
    if (result) context.next();
  }
  function continueStep(): Promise<void> {
    return creating.value ? createTables() : continueEditor();
  }

  defineExpose({ ready, continue: continueStep, nextLabel });
</script>

<template>
  <div class="reservations-guide-step-tables">
    <template v-if="creating">
      <h2 class="step-title">
        {{ $t('reservations.guide.tables.createTitle') }}
      </h2>
      <PosUiEmptyState
        v-if="!canCreate"
        icon="table-chair"
        :title="$t('reservations.guide.tables.noTablesTitle')"
        :description="$t('reservations.guide.tables.noTablesText')"
      />
      <template v-else>
        <p class="step-help">
          {{ $t('reservations.guide.tables.createHelp') }}
        </p>
        <V2ReservationsGuideCreateTables
          :count="createPayload.count"
          :seats="createPayload.seats"
          :can-create="canCreate"
          :busy="busy"
          :progress="creator.progress.value ?? undefined"
          :failed="creator.failed.value"
          @change="onCreateChange"
          @retry="createTables"
        />
      </template>
    </template>
    <template v-else-if="configuration">
      <h2 class="step-title">{{ $t('reservations.guide.tables.title') }}</h2>
      <p class="step-help">{{ $t(helpKey, { seats: DEFAULT_TABLE_SEATS }) }}</p>
      <V2ReservationTablesEditor
        :configuration="configuration"
        :draft="context.tablesDraft"
        :floor-plan="context.floorPlan"
        mode-control="toggle"
        :all-seats="allSeats"
        :excluded-ids="excludedIds"
        :disabled="busy"
        @update:allSeats="context.allSeats.value = $event"
        @undo-exclusions="context.clearExcluded()"
        @touched="context.clearExcluded($event)"
      />
    </template>
  </div>
</template>

<style scoped lang="scss">
  .reservations-guide-step-tables {
    @apply tw-flex tw-flex-col tw-gap-4;
    p {
      @apply tw-m-0;
    }
    .step-title {
      @apply tw-m-0 tw-text-xl tw-font-semibold tw-tracking-tight tw-text-neutral-900;
    }
    .step-help {
      @apply tw-text-sm tw-leading-relaxed tw-text-neutral-500;
    }
  }
</style>
